import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextInput from "../../components/TextInput/TextInput";
import Button from "../../components/Button/Button";
import LeftPanel from "../../components/LeftPanel/LeftPanel";
import OnboardingStepHeader from "../../components/OnboardingStepHeader/OnboardingStepHeader";
import { saveStep4Data, goToNextStep, goToPreviousStep, setStep4Data } from "../../redux/slices/OfficeOnboardingSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { logger } from "../../utils/logger";
import { OfficeService } from "../../services/office.service";

const log = logger.getLogger("OnboardingStep4");

const Step4: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { step4Data, isLoading } = useSelector((state: RootState) => state.officeOnboarding);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);

  const officeId = currentUser?.offices?.[0]?.officeId || "";

  const [workNoteName, setWorkNoteName] = useState("Work Note");
  const [workNotePrice, setWorkNotePrice] = useState(step4Data?.workNotePrice?.toString() || "26");
  const [prescriptionRefillName, setPrescriptionRefillName] = useState("Prescription Refill");
  const [prescriptionRefillPrice, setPrescriptionRefillPrice] = useState(step4Data?.prescriptionRefillPrice?.toString() || "20.75");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (step4Data) {
      setWorkNotePrice(step4Data.workNotePrice?.toString() || "26");
      setPrescriptionRefillPrice(step4Data.prescriptionRefillPrice?.toString() || "20.75");
    }
  }, [step4Data]);

  const validatePrice = (value: string): string => {
    // Must not be empty
    if (!value || !value.trim()) {
      return "Price is required.";
    }

    const trimmedValue = value.trim();

    // Check for invalid characters (only numbers and decimal point allowed)
    if (!/^\d*\.?\d*$/.test(trimmedValue)) {
      return "Please enter a valid price.";
    }

    // Must be numeric
    const numValue = parseFloat(trimmedValue);
    if (isNaN(numValue)) {
      return "Please enter a valid price.";
    }

    // Must be greater than 0
    // if (numValue <= 0) {
    //   return "Price must be greater than $0.";
    // }

    // Maximum price limit (set to a reasonable limit, e.g., $999,999.99)
    const MAX_PRICE = 999999.99;
    if (numValue > MAX_PRICE) {
      return "Price cannot exceed the allowed limit.";
    }

    return "";
  };

  const handlePriceChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      if (field === "workNote") {
        setWorkNotePrice(value);
      } else {
        setPrescriptionRefillPrice(value);
      }
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: "" }));
      }
    }
  };

  const handlePriceBlur = (field: string) => () => {
    const value = field === "workNote" ? workNotePrice : prescriptionRefillPrice;
    const error = validatePrice(value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleBack = () => {
    dispatch(goToPreviousStep());
  };

  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }
    const workNoteError = validatePrice(workNotePrice);
    const prescriptionRefillError = validatePrice(prescriptionRefillPrice);

    if (workNoteError || prescriptionRefillError) {
      setErrors({
        workNote: workNoteError,
        prescriptionRefill: prescriptionRefillError,
      });
      return;
    }

    if (!officeId) {
      setErrors({ general: "Office ID not found. Please try again." });
      return;
    }

    setIsSaving(true);
    setErrors({});
    setIsSubmitting(true);
    try {
      // Prepare services for bulk creation
      const services = [
        {
          officeId,
          name: workNoteName,
          price: Math.round(parseFloat(workNotePrice) * 100), // Convert to cents
        },
        {
          officeId,
          name: prescriptionRefillName,
          price: Math.round(parseFloat(prescriptionRefillPrice) * 100), // Convert to cents
        },
      ];

      // Add services using V2 bulk API
      const result = await OfficeService.addOfficeServicesV2(officeId, services);

      if (!result) {
        setErrors({ general: "Failed to create services. Please try again." });
        setIsSaving(false);
        return;
      }

      // Save to local state
      const step4Payload = {
        workNotePrice: parseFloat(workNotePrice),
        prescriptionRefillPrice: parseFloat(prescriptionRefillPrice),
      };

      await dispatch(saveStep4Data({ data: step4Payload, officeId: officeId })).unwrap();
      dispatch(setStep4Data(step4Payload));
      dispatch(goToNextStep());
    } catch (err: any) {
      log.error("Failed to save step 4", err);
      // Check if it's a duplicate service error
      const errorMessage = err?.message || err?.toString() || "";
      if (errorMessage.toLowerCase().includes("exists") || errorMessage.toLowerCase().includes("duplicate")) {
        setErrors({ general: "One or more services already exist. You can update them in the Services section." });
      } else {
        setErrors({ general: "Failed to create services. Please try again." });
      }
    } finally {
      setIsSubmitting(false);
      setIsSaving(false);
    }
  };

  const handleVideoPlay = () => {
    log.debug("Video play clicked");
  };

  return (
    <div className="flex h-full min-h-0">
      {/* Left Panel */}
      <LeftPanel onVideoPlay={handleVideoPlay} />

      {/* Right Panel */}
      <div className="w-full md:w-2/3 bg-white flex flex-col h-full min-h-0">
        {/* Sticky Header with Progress */}
        <div className="sticky top-0 z-10 bg-white flex-shrink-0">
          <OnboardingStepHeader currentStep={4} totalSteps={5} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex justify-center">
            <div className="p-8 w-full max-w-4xl">
              {/* Title and Description */}
              <div className="mb-2">
                <h1 className="text-base leading-normal tracking-[-0.31px] mb-2">Set Your Service Pricing</h1>
                <p className="text-gray-500 tracking-[-0.31px]">
                  We&apos;ve pre-filled the two most common uninsured services to help you get started quickly. Default pricing is based on OMA
                  guidelines. Adjust as needed.
                </p>
              </div>

              {/* Info Box */}
              <div className="mb-10 bg-blue-50 border border-blue-200 rounded-xl p-4 text-[#1C398E] text-sm tracking-[-0.15px]">
                You can add all your services and customize pricing within the Services section of your office.
              </div>

              {/* Pricing Table */}
              <div className="mb-6">
                <div className="border border-gray-200 rounded-lg overflow-hidden max-w-[526px]">
                  {/* Table Header */}
                  <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-2 gap-4 text-sm tracking-[-0.15px]">
                      <div>Service Name</div>
                      <div>Price</div>
                    </div>
                  </div>

                  {/* Work Note Row */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="grid grid-cols-2 gap-4 items-center">
                      <div>
                        <input
                          type="text"
                          value={workNoteName}
                          onChange={e => setWorkNoteName(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-100 rounded-lg border-0 focus:outline-none h-10 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center bg-gray-100 rounded-lg px-4">
                          <span className="text-gray-500 mr-1">$</span>
                          <input
                            type="text"
                            value={workNotePrice}
                            onChange={handlePriceChange("workNote")}
                            onBlur={handlePriceBlur("workNote")}
                            className="w-full bg-transparent border-0 focus:outline-none h-10 text-sm"
                          />
                        </div>
                        {errors.workNote && <p className="text-sm text-red-500 mt-1">{errors.workNote}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Prescription Refill Row */}
                  <div className="px-6 py-4">
                    <div className="grid grid-cols-2 gap-4 items-center">
                      <div>
                        <input
                          type="text"
                          value={prescriptionRefillName}
                          onChange={e => setPrescriptionRefillName(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-100 rounded-lg border-0 focus:outline-none h-10 text-sm"
                        />
                      </div>
                      <div>
                        <div className="flex items-center bg-gray-100 rounded-lg px-4">
                          <span className="text-gray-500 mr-1">$</span>
                          <input
                            type="text"
                            value={prescriptionRefillPrice}
                            onChange={handlePriceChange("prescriptionRefill")}
                            onBlur={handlePriceBlur("prescriptionRefill")}
                            className="w-full bg-transparent border-0 focus:outline-none h-10 text-sm"
                          />
                        </div>
                        {errors.prescriptionRefill && <p className="text-sm text-red-500 mt-1">{errors.prescriptionRefill}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {errors.general && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{errors.general}</div>}
            </div>
          </div>
        </div>

        {/* Sticky Footer with Navigation Buttons */}
        <div className="sticky bottom-0 z-10 bg-white flex-shrink-0 p-8">
          <div className="flex justify-between">
            <Button text="Back" onClick={handleBack} variant="Tertiary" width="fit" className="px-8" buttonStyle="square" />
            <Button
              text="Save & Continue"
              onClick={handleSubmit}
              disabled={isLoading || isSaving || isSubmitting}
              variant={isLoading || isSaving || isSubmitting ? "loading" : "Primary"}
              width="fit"
              className="px-8"
              buttonStyle="square"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step4;
